import { recoveryAead } from '../crypto/recoveryAead';
import { api } from './api';

/**
 * Clients for the account-recovery backup flow.
 *
 * The BE (services/api/internal/recovery/service.go) stores an opaque
 * ciphertext blob per account, capped at 256 KB. The client encrypts the
 * SDK identity export with a user-chosen passphrase (or recovery key) so
 * the BE never sees the plaintext. On recovery, a separate auth route
 * (`POST /v1/auth/recover/verify`) mints a recovery JWT that gates
 * `GET /v1/recovery/blobs`.
 *
 * Encryption shape (versioned envelope, so we can rotate KDF/AEAD later):
 *
 *   {
 *     "v": 1,
 *     "kdf": "PBKDF2-SHA256",
 *     "iter": 600000,
 *     "salt": base64url(16 bytes),
 *     "alg": "AES-256-GCM",
 *     "iv":  base64url(12 bytes),
 *     "ct":  base64url(ciphertext || auth_tag)
 *   }
 *
 * The envelope is JSON-serialized then base64url-encoded as `ciphertext`.
 * Helpers below encode/decode that envelope so callers only need a
 * passphrase and the cleartext identity export bytes.
 */
export interface PostBlobRequest {
  /** Base64url-encoded ciphertext (the envelope JSON, base64url-encoded). */
  ciphertext: string;
}

export interface GetBlobResponse {
  ciphertext: string;
  updated_at: string;
  size_bytes: number;
}

export async function postRecoveryBlob(req: PostBlobRequest): Promise<void> {
  await api.post<void>('/v1/recovery/blobs', req);
}

/**
 * Download the encrypted backup. Requires a **recovery JWT** in
 * `Authorization` — not the device JWT used for everything else. The
 * recovery JWT comes from `POST /v1/auth/recover/verify` (an OTP flow on
 * `services/api/internal/auth/service.go:RecoverVerify`).
 *
 * For testing we accept an explicit token override so callers can pass a
 * recovery JWT minted out-of-band (`pingcli auth recover` etc.) without
 * the global `api` interceptor swapping it back to the device JWT.
 */
export async function getRecoveryBlob(opts: {
  recoveryJwt?: string;
  signal?: AbortSignal;
} = {}): Promise<GetBlobResponse> {
  const headers: Record<string, string> = {};
  if (opts.recoveryJwt) headers.Authorization = `Bearer ${opts.recoveryJwt}`;
  const reqOpts: { headers?: Record<string, string>; signal?: AbortSignal } = {};
  if (Object.keys(headers).length > 0) reqOpts.headers = headers;
  if (opts.signal) reqOpts.signal = opts.signal;
  return api.get<GetBlobResponse>('/v1/recovery/blobs', reqOpts);
}

// --- encryption helpers ---------------------------------------------------
//
// Cleartext envelope shape lives in `../crypto/recoveryAead`. The
// platform-extension resolver swaps the AEAD impl per host (web →
// `crypto.subtle`, RN-macOS → `@noble/*`) so the same wire bytes round-
// trip across surfaces.

/**
 * Encrypt `identityExport` with `passphrase` and return the
 * base64url-encoded envelope ready to send as `ciphertext` to
 * `POST /v1/recovery/blobs`. Delegates to the platform-resolved
 * `recoveryAead` (web → `crypto.subtle`, RN-macOS → `@noble/*`)
 * so the same wire format works on every host.
 */
export async function encryptRecoveryBlob(
  identityExport: Uint8Array,
  passphrase: string,
): Promise<string> {
  return recoveryAead.encrypt(identityExport, passphrase);
}

/**
 * Decrypt the base64url-encoded `ciphertext` returned by
 * `GET /v1/recovery/blobs`. Throws on malformed envelopes, bad
 * passphrases, or unrecognised versions — the caller surfaces a
 * user-friendly "incorrect passphrase" message.
 */
export async function decryptRecoveryBlob(
  ciphertext: string,
  passphrase: string,
): Promise<Uint8Array> {
  return recoveryAead.decrypt(ciphertext, passphrase);
}
